/**
 * App-wide magic numbers and limits.
 * Import from '@/lib/constants' instead of hardcoding values in components.
 */

/** How long a toast notification stays visible (ms) */
export const TOAST_DURATION_MS = 4000

/** Max height of the chat textarea before it scrolls (px) */
export const TEXTAREA_MAX_HEIGHT_PX = 150

/** Free-tier monthly message limit (fallback if server doesn't report) */
export const FREE_TIER_MESSAGE_LIMIT = 20

/** Minimum password length enforced on signup + reset */
export const MIN_PASSWORD_LENGTH = 6

/** localStorage key for the pending referral code captured from ?ref= */
export const REFERRAL_CODE_STORAGE_KEY = 'illuminateRefCode'

/** localStorage key for UTM params captured on first landing. Stored as JSON
 *  and passed through to signup metadata so raw_user_meta_data carries the
 *  source attribution (utm_source, utm_medium, utm_campaign, utm_content). */
export const UTM_STORAGE_KEY = 'illuminateUTM'

/** Reset-password flow timers (ms) */
export const RESET_LINK_VERIFY_DELAY_MS = 1500
export const POST_RESET_REDIRECT_DELAY_MS = 3000

/** Message index after which the upgrade teaser appears in chat */
export const UPGRADE_TEASER_AFTER_INDEX = 5

/** Max saved prayers kept in localStorage */
export const MAX_SAVED_PRAYERS = 50

/** Max character lengths for user-facing text inputs */
export const MAX_CHAT_MESSAGE_LENGTH = 5000
export const MAX_BIBLE_NOTE_LENGTH = 1000
export const MAX_REFLECTION_LENGTH = 2000
export const MAX_PRAYER_SITUATION_LENGTH = 1500
export const MAX_BLOG_CONTENT_LENGTH = 50000

/** Chat image attachments (+ button)
 *  CHAT_ATTACHMENT_BUCKET and CHAT_MAX_ATTACHMENTS_PER_MESSAGE are mirrored
 *  in supabase/functions/chat/index.ts (CHAT_ATTACHMENT_BUCKET +
 *  MAX_ATTACHMENTS_PER_MESSAGE). Edge function uses a separate
 *  ATTACHMENT_SIGNED_URL_SECONDS = 600 (Claude fetch window) — different
 *  intent from this 3600 (browser render window). Change one, change the
 *  other in the same commit. */
export const CHAT_ATTACHMENT_BUCKET = 'chat-attachments'
export const CHAT_MAX_ATTACHMENTS_PER_MESSAGE = 3
export const CHAT_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024 // 10 MB — matches storage bucket cap
export const CHAT_ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
]
export const CHAT_ATTACHMENT_SIGNED_URL_SECONDS = 3600 // 1 hour — signed URL for rendering

/**
 * z-index for the floating verse-actions pill in BibleVerseActions.
 * Near int-max so it floats above every React portal and browser chrome
 * (mobile viewports sometimes render sticky toolbars at very high stacking
 * contexts). Do not use this elsewhere — it's deliberately the "nuclear" layer.
 */
export const Z_INDEX_FLOATING_ACTIONS = 2147483646

/** Max characters allowed in a user-edited highlight-key label. */
export const MAX_HIGHLIGHT_LABEL_LEN = 24

/** How long the gold ring flashes on a verse after "jump to" navigation (ms). */
export const VERSE_FLASH_DURATION_MS = 1500

/** Time math constants used by BibleNotesTab's `formatRelative` / `wasEdited`. */
export const MS_PER_MINUTE = 60_000
export const HOURS_PER_DAY = 24
export const DAYS_PER_MONTH = 30
export const MONTHS_PER_YEAR = 12
/** Buffer swallowed by `wasEdited` so brand-new notes don't show "edited"
 *  just because the upsert clock skew ticked the updated_at forward. */
export const EDIT_BUFFER_MS = 60_000

/**
 * User-facing error messages for the invite-code redemption RPC.
 * Keys are the `reason` values returned by `redeem_beta_invite` in Postgres
 * (RPC name retained for stability — see auth.js `redeemInviteCode`).
 * Used by ProfileContext's `redeemInviteCode` wrapper.
 */
export const INVITE_CODE_ERROR_MESSAGES = {
  expired: 'This code has expired.',
  exhausted: 'This code has been fully redeemed.',
  already_redeemed: 'You already have premium access.',
  already_premium: 'You already have premium access.',
  not_authenticated: 'Please sign in to redeem a code.',
  invalid: 'Invalid invite code.',
}
